"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.computeSubjectToSatelliteCaps = computeSubjectToSatelliteCaps;
exports.computeContainerClock = computeContainerClock;
const date_1 = require("encamp-shared/dist/utils/date");
const formatMoveWindow_1 = require("encamp-shared/dist/waste/formatMoveWindow");
const adapter_1 = require("./thresholds/adapter");
const EMPTY_CLOCK = {
    limits: [],
    clockStartDate: null,
    effectiveDeadlineDate: null,
    undetermined: null,
};
/**
 * The situation a container's clock reads. The stream's classification is
 * left out: nothing on the clock depends on it.
 */
function containerSituation(container, areaType, facility) {
    return {
        facility: {
            state: facility.state,
            ...(facility.generatorStatus === null
                ? {}
                : { federalGeneratorStatus: facility.generatorStatus }),
            ...(facility.stateGeneratorStatus === null
                ? {}
                : { stateGeneratorStatus: facility.stateGeneratorStatus }),
        },
        area: { type: areaType },
        container: {
            wasteKind: container.wasteKind,
            managedAsUniversalWaste: container.managedAsUniversalWaste,
            operatesUnderSubpartP: facility.operatesUnderSubpartP,
            mustTransport200Miles: container.sqgOver200Miles,
            attachedToActiveEpisodicEvent: container.activeEpisodicEvent !== null,
        },
    };
}
/**
 * The day an anchor event happened for this container, floored to UTC
 * midnight; null when it hasn't.
 *
 * The satellite move-window deadline (area.moveDeadline) is a genuine
 * timestamp owned by the area rollup and is deliberately NOT floored.
 * TODO - we should actually remove the timestamp behavior and switch to calendar days
 *        confirmed by Jess in 2026-08-18 meeting with waste pod
 */
function observedAnchorDate(container, anchor) {
    const observed = rawAnchorDate(container, anchor);
    return observed === null ? null : (0, date_1.floorToUtcMidnight)(observed);
}
function rawAnchorDate(container, anchor) {
    switch (anchor) {
        case "ACCUMULATION_START":
        case "CENTRAL_ARRIVAL":
            return container.anchorDates[anchor];
        case "EPISODIC_START":
            return container.activeEpisodicEvent?.startDate ?? null;
        case "FULL":
            // Known gap in at least MA, VT, and CA; the first rule anchored here
            // needs a fill-date input on the container.
            return null;
    }
}
/** The event a DAYS_SINCE threshold counts from, when it is one; null otherwise. */
function eventAnchorOf(threshold) {
    if (threshold.kind !== "DAYS_SINCE" || typeof threshold.from !== "string") {
        return null;
    }
    return threshold.from;
}
function buildRuleQuery(container, areaType, facility) {
    return {
        state: facility.state,
        generatorStatus: facility.generatorStatus,
        areaType,
        mustTransport200Miles: container.sqgOver200Miles,
        stateGeneratorStatus: facility.stateGeneratorStatus,
        wasteKind: container.wasteKind ?? null,
        operatesUnderSubpartP: facility.operatesUnderSubpartP,
        managedAsUniversalWaste: container.managedAsUniversalWaste,
    };
}
/** A container's thresholds and its area's, as the jurisdiction defines them. */
function thresholdsAt(rules, situation) {
    return {
        container: rules.thresholdsFor(situation),
        area: rules.thresholdsFor({ facility: situation.facility, area: situation.area }),
    };
}
/**
 * Whether the satellite volume caps reach this container — resolved
 * independently of the container's CURRENT area, so the answer is stable
 * across area moves: a central container carries the value it would have if
 * it were sitting in a satellite area right now. Persisted by
 * `persistContainerClock` onto `WasteContainer.subjectToSatelliteCaps`, the
 * single source the clock-bucket SQL reads instead of re-deriving the VSQG
 * carve-out, the subclassification exemptions, the Subpart P gate, and
 * episodic relief itself.
 *
 * A sibling of `computeContainerClock` rather than a field on its result:
 * the flag depends on the waste, its episodic attachment, and the facility,
 * not on the container's current area or lifecycle status, so it stays
 * defined even for a SHIPPED or unplaced container that
 * `computeContainerClock` short-circuits on.
 */
function computeSubjectToSatelliteCaps(container, facility) {
    const rules = (0, adapter_1.thresholdRulesFor)(facility.state);
    const thresholds = thresholdsAt(rules, containerSituation(container, "SATELLITE", facility));
    const resolved = (0, adapter_1.rulesetFromThresholds)(rules, "SATELLITE", thresholds.container, thresholds.area);
    return resolved.areaType === "SATELLITE" && resolved.caps.kind === "VOLUME";
}
function buildVolumeLimit(caps) {
    return {
        limitType: "VOLUME",
        basis: `SAA ${caps.nonAcuteGal} gal / ${caps.acuteGal} gal (${caps.acuteKg} kg) acute`,
        citation: caps.citation,
        jurisdiction: caps.jurisdiction,
        deadlineDate: null,
        totalDays: null,
        isMoveWindow: false,
    };
}
function buildMoveWindowLimit(caps, moveDeadline) {
    return {
        limitType: "TIME",
        basis: `SAA move window, ${(0, formatMoveWindow_1.formatMoveWindow)(caps.moveWindowDays)}`,
        citation: caps.citation,
        jurisdiction: caps.jurisdiction,
        deadlineDate: moveDeadline,
        totalDays: caps.moveWindowDays,
        isMoveWindow: true,
    };
}
function buildTimeLimit(limit, query, anchor, label) {
    return {
        limitType: "TIME",
        basis: `${limit.days} days — ${label}, ${query.areaType.toLowerCase()}`,
        citation: limit.citation,
        jurisdiction: limit.jurisdiction,
        deadlineDate: (0, date_1.addUtcDays)(anchor, limit.days),
        totalDays: limit.days,
        isMoveWindow: false,
    };
}
/** The competing time limit whose deadline binds. A missing deadline
 * (never true of the two callers' limits, both built with one) yields to the
 * other; ties keep `a`, so the caller decides the tiebreak by argument order. */
function soonerTimeLimit(a, b) {
    if (a.deadlineDate === null) {
        return b;
    }
    if (b.deadlineDate === null) {
        return a;
    }
    return b.deadlineDate.getTime() < a.deadlineDate.getTime() ? b : a;
}
// Just the length. Every other limit's basis names the tier and area that
// chose it ("180 days — SQG, central") because those are what a reader would
// otherwise have to look up; an episodic limit has no such qualifier, and the
// citation and the rule-source badge beside it already say whose rule it is.
function buildEpisodicLimit(window, anchorDate) {
    if (window.kind !== "DAYS_SINCE" || window.determination !== "KNOWN") {
        throw new Error(`${window.id} is not a known episodic window.`);
    }
    return {
        limitType: "TIME",
        basis: `${window.value} days`,
        citation: window.source.citation,
        jurisdiction: window.source.jurisdiction,
        deadlineDate: (0, date_1.addUtcDays)(anchorDate, window.value),
        totalDays: window.value,
        isMoveWindow: false,
    };
}
/** The episodic window as a limit, when the container is attached and the window applies. */
function episodicLimitOf(container, containerThresholds) {
    const window = containerThresholds.find(adapter_1.isEpisodicWindow);
    const anchorDate = observedAnchorDate(container, "EPISODIC_START");
    if (window === undefined || window.determination !== "KNOWN" || anchorDate === null) {
        return null;
    }
    return buildEpisodicLimit(window, anchorDate);
}
function computeContainerClock(container, area, facility) {
    if (container.status === "SHIPPED" || container.archivedAt !== null || area === null) {
        return EMPTY_CLOCK;
    }
    const rules = (0, adapter_1.thresholdRulesFor)(facility.state);
    const thresholds = thresholdsAt(rules, containerSituation(container, area.type, facility));
    const resolved = (0, adapter_1.rulesetFromThresholds)(rules, area.type, thresholds.container, thresholds.area);
    const timeLimit = (0, adapter_1.timeLimitOf)(thresholds.container);
    // With no standing clock (a satellite container under part 262), the clock
    // start still says how long the container has been where it is.
    const anchorEvent = timeLimit === null
        ? area.type === "CENTRAL"
            ? "CENTRAL_ARRIVAL"
            : "ACCUMULATION_START"
        : eventAnchorOf(timeLimit);
    const anchor = anchorEvent === null ? null : observedAnchorDate(container, anchorEvent);
    const query = buildRuleQuery(container, area.type, facility);
    const limits = [];
    if (resolved.areaType === "SATELLITE" && resolved.caps.kind === "VOLUME") {
        limits.push(buildVolumeLimit(resolved.caps));
        if (area.isOverThreshold && area.moveDeadline !== null) {
            limits.push(buildMoveWindowLimit(resolved.caps, area.moveDeadline));
        }
    }
    // The anchor, not the generator status, gates the deadline: a state's
    // standing satellite clock (CA's one-year limit) resolves even with no
    // resolvable status, while a null anchor means no ADDs in the current
    // cycle — nothing to date a deadline from.
    const standing = resolved.clock?.kind === "TIME" && timeLimit !== null && anchor !== null
        ? buildTimeLimit(resolved.clock, query, anchor, (0, adapter_1.timeBasisLabel)(timeLimit, query))
        : null;
    // The episodic window carries its own anchor (the event's start), so it
    // exists even where the container has none — a VSQG's central container, a
    // federal satellite container. The rules withhold it where a
    // subclassification program governs the clock, and in states that never adopted subpart L.
    const episodic = episodicLimitOf(container, thresholds.container);
    // The standing clock and the episodic overlay compete for one obligation —
    // how long this waste may stay — so only the one whose deadline binds is
    // emitted: `limits` carries what APPLIES, and a superseded time limit
    // applies to nothing. Taking the earlier is the whole of "episodic
    // supersedes the standing clock" without ever EXTENDING a deadline — the
    // reading the attach guard exists to prevent. Everything else in `limits`
    // is a separate obligation and stays.
    if (standing !== null && episodic !== null) {
        limits.push(soonerTimeLimit(episodic, standing));
    }
    else if (standing !== null) {
        limits.push(standing);
    }
    else if (episodic !== null) {
        limits.push(episodic);
    }
    const timeDeadlines = limits
        .map((limit) => limit.deadlineDate)
        .filter((deadline) => deadline !== null);
    const effectiveDeadlineDate = timeDeadlines.length > 0
        ? new Date(Math.min(...timeDeadlines.map((deadline) => deadline.getTime())))
        : null;
    // The rules' own UNDETERMINED, passed through with the payload the
    // jurisdiction composed at the point it decided it couldn't answer.
    const undetermined = resolved.clock?.kind === "UNDETERMINED"
        ? { missingAxes: resolved.clock.missingAxes, reason: resolved.clock.reason }
        : null;
    return {
        limits,
        clockStartDate: anchor,
        effectiveDeadlineDate,
        undetermined,
    };
}
//# sourceMappingURL=computeContainerClock.js.map